import React from "react";
import { motion } from "framer-motion";
import { pageVariants } from "../animations/pageTransitions";
import "./Legals.css";

/**
 * Quantisys360 — EHI Export Documentation (170.315(b)(10))
 * Publicly accessible documentation page required for attestation.
 */

const EhiExportDocumentation = () => {
  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
      <div className="legal-container">
        <h1>Electronic Health Information (EHI) Export Documentation</h1>

        <p className="legal-note">
          This document explains how <strong>Quantisys360</strong> supports exporting electronic health
          information (EHI). It is written in plain language for providers and practice administrators.
        </p>

        <p className="legal-note">
          This page serves as the publicly accessible documentation describing the format and structure
          of exported EHI for Quantisys360.
        </p>

        <h2>What is EHI Export?</h2>
        <p>
          EHI Export lets a provider export electronic health information for an individual patient or,
          when enabled, for a group of patients (a patient population). This can be used for transitions
          of care, switching systems, record requests, or operational needs.
        </p>

        <p className="legal-note">
          Electronic Health Information (EHI) refers to electronic protected health information (ePHI)
          included in a designated record set, as defined under applicable regulations.
        </p>

        <h2>Who can request an export?</h2>
        <div className="legal-highlight">
          <p>✔ Practice Administrators (or authorized staff with export permissions)</p>
          <p>✔ Authorized users based on role-based access controls configured by the practice</p>
          <p className="legal-note">
            Export permissions are controlled by your organization’s security settings. If a user does not
            have export permissions, they will not be able to initiate an export.
          </p>
          <p className="legal-note">
            Authorized users can perform single patient EHI exports at any time without developer assistance.
          </p>
        </div>

        <h2>What information is included?</h2>
        <p>
          The EHI export functionality includes all electronic health information (EHI) that can be stored
          by Quantisys360 at the time of export, in accordance with §170.315(b)(10).
        </p>

        <p>
          EHI Export is intended to include the electronic health information available in Quantisys360 that
          is associated with the patient(s) selected for export. The exported content may include, when present:
        </p>

        <div className="legal-highlight">
          <p>✔ Patient demographics</p>
          <p>✔ Problems/diagnoses</p>
          <p>✔ Allergies</p>
          <p>✔ Medications</p>
          <p>✔ Immunizations</p>
          <p>✔ Vital signs</p>
          <p>✔ Laboratory results</p>
          <p>✔ Clinical notes and documents</p>
          <p>✔ Care plans</p>
          <p>✔ Other electronic health information maintained in the designated record set and stored by Quantisys360 at the time of export</p>
          <p className="legal-note">
            The exact content exported depends on the data stored in Quantisys360 for the patient or patient population at the time of export.
          </p>
        </div>

        <h2>Export types supported</h2>
        <div className="legal-highlight">
          <p>
            <strong>1) Patient-level export:</strong> Export EHI for a single patient record.
          </p>
          <p>
            <strong>2) Patient population export:</strong> Export EHI for multiple patients, including all patients when requested,
            based on the data stored in Quantisys360 at the time of export.
          </p>
        </div>

        <h2>Export formats</h2>
        <p>
          Quantisys360 provides export output in electronic formats depending on the requested scope.
        </p>

        <p className="legal-note">
          Exported files are provided in electronic, machine-readable, and computable formats where applicable.
        </p>

        <div className="legal-highlight">
          <p>✔ Excel files (.xlsx) for structured, machine-readable data</p>
          <p>✔ PDF files for human-readable documentation and clinical summaries</p>
          <p className="legal-note">
            Excel files are provided in a structured format that can be processed by other software systems.
            PDF files are provided for readability and documentation purposes and may not be machine-readable in all cases.
          </p>
        </div>

        <h2>How to perform an EHI Export</h2>
        <p className="legal-note">
          The steps below describe a typical export flow. Menu labels may vary based on your organization’s configuration.
        </p>

        <div className="legal-highlight">
          <p><strong>Step 1:</strong> Sign in to Quantisys360 with an authorized account.</p>
          <p><strong>Step 2:</strong> Navigate to the patient record for a single patient export, or to the administrative export area for a patient population export.</p>
          <p><strong>Step 3:</strong> Select <em>Export</em> and choose the export type and available format options.</p>
          <p><strong>Step 4:</strong> Confirm the request and submit the export.</p>
          <p><strong>Step 5:</strong> Download the export file(s) when processing is complete, or retrieve them from the designated secure location if configured by the organization.</p>
        </div>

        <p className="legal-note">
          Authorized users can initiate a single patient export at any time the user chooses without developer assistance.
        </p>

        <h2>How long does an export take?</h2>
        <p>
          Export time depends on the amount of data being exported, the number of patients selected for population exports,
          and overall system load.
        </p>

        <p className="legal-note">
          EHI exports are generated in a timely manner, typically near real-time depending on system load and data volume.
        </p>

        <div className="legal-highlight">
          <p>✔ Small patient-level exports: typically minutes</p>
          <p>✔ Larger patient population exports: may take longer depending on data volume</p>
        </div>

        <h2>Where is the export delivered?</h2>
        <div className="legal-highlight">
          <p>✔ Downloaded directly by the authorized user</p>
          <p>✔ Delivered to a practice-designated secure storage location, if configured</p>
          <p className="legal-note">
            Exported files should be stored and transmitted securely. Practices are responsible for handling exported files in
            accordance with applicable privacy and security requirements.
          </p>
        </div>

        <h2>Security and access controls</h2>
        <div className="legal-highlight">
          <p>✔ Role-based permissions restrict which users can export EHI</p>
          <p>✔ Audit logging may record export actions performed by users</p>
          <p>✔ Exported data should be treated as sensitive patient information</p>
        </div>

        <h2>Fees, constraints, and limitations</h2>
        <p className="legal-note">
          Standard EHI export functionality is included with the Quantisys360 subscription and does not require additional fees.
          Additional services such as large-scale migrations, custom formatting, or custom data conversion services may incur
          one-time service fees depending on scope.
        </p>

        <div className="legal-highlight">
          <p>✔ Standard single patient EHI export: included at no additional charge</p>
          <p>✔ Standard patient population EHI export: included at no additional charge</p>
          <p>✔ Custom migration, conversion, or special export services: may require a one-time service fee depending on scope</p>
        </div>

        <h2>Terms of Use</h2>
        <p>
          EHI export functionality is intended for authorized use by customers and their permitted workforce members in accordance
          with applicable law, customer agreements, and organizational policies. Users are responsible for safeguarding exported files
          and ensuring they are used, transmitted, and stored appropriately.
        </p>

        <h2>Support</h2>
        <p className="legal-contact">
          If you need help understanding how to use the EHI export functionality in Quantisys360, contact{" "}
          <a href="mailto:contact@sshealthcareconsulting.com">contact@sshealthcareconsulting.com</a>.
        </p>
      </div>
    </motion.div>
  );
};

export default EhiExportDocumentation;